import { createContext, useContext } from "react";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { noop } from "@common/helpers/function";

export interface UpgradeModalCustomizationOptions {
    title?: React.ReactNode;
    onHide?: () => void;
}

export interface UpgradeModalParams extends UpgradeModalCustomizationOptions {
    plan: FLAT_RATE_PLANS;
    isTrial: boolean;
}

interface CheckoutData {
    clientSecret: string;
    intentId: string;
}

export interface PlanManagementContextValue {
    upgrade(
        plan: FLAT_RATE_PLANS,
        isTrial: boolean,
        options?: UpgradeModalCustomizationOptions,
    ): void;
    downgrade(plan: FLAT_RATE_PLANS): void;
    convert(): void;
}

export interface PlanManagementModalContextValue {
    upgradeModal: UpgradeModalParams | null;
    downgradeModal: FLAT_RATE_PLANS | null;
    convertModal: boolean;
    checkoutData: CheckoutData | undefined;
    onHideUpgrade: () => void;
    onHideDowngrade: () => void;
    onHideConvert: () => void;
    registerAsModalHost: (id: string) => () => void;
    subscribeToHostChanges: (listener: () => void) => () => void;
    getActiveHostId: () => string | null;
}

export const planManagementContext = createContext<PlanManagementContextValue>({
    downgrade: noop,
    upgrade: noop,
    convert: noop,
});

export const planManagementModalContext =
    createContext<PlanManagementModalContextValue>({
        upgradeModal: null,
        downgradeModal: null,
        convertModal: false,
        checkoutData: undefined,
        onHideUpgrade: noop,
        onHideDowngrade: noop,
        onHideConvert: noop,
        registerAsModalHost: () => noop,
        subscribeToHostChanges: () => noop,
        getActiveHostId: () => null,
    });

export function usePlanManagement() {
    const ctx = useContext(planManagementContext);

    if (!ctx) {
        throw new Error(
            "usePlanManagement must be used within a PlanManagementProvider",
        );
    }

    return ctx;
}

export function usePlanManagementModal() {
    return useContext(planManagementModalContext);
}
